import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';

// Theme initialization
import { initializeTheme } from './config/themes';

// Auth Provider
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute, PublicRoute } from './components/ProtectedRoute';

// Layout
import Layout from './components/Layout';
import { FinanceRoute } from './components/FinanceRoute';

// Core Pages
import CommandCentre from './pages/CommandCentre';
import LoadingScreen from './pages/LoadingScreen';
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Finance Module (includes Supplier Upload / Accounting)
import Finance from './pages/finance/Finance';
import Pots from './pages/finance/Pots';
import Cashflow from './pages/finance/Cashflow';
import FinanceInvoices from './pages/finance/FinanceInvoices';
import SupplierUpload from './pages/finance/SupplierUpload';
import FinanceXero from './pages/finance/FinanceXero';
import Margin from './pages/finance/Margin';
import DocumentUpload from './pages/finance/DocumentUpload';
import PriceIntelligence from './pages/finance/PriceIntelligence';
import BusinessReality from './pages/finance/BusinessReality';

// Jobs Module
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';

// Quote Life Module (includes Estimates & Proposals)
import Quotes from './pages/Quotes';
import QuoteDetail from './pages/QuoteDetail';
import Estimates from './pages/Estimates';
import EstimateDetail from './pages/EstimateDetail';
import Proposals from './pages/Proposals';
import ProposalDetail from './pages/ProposalDetail';

// Invoices Module
import Invoices from './pages/Invoices';
import InvoiceDetail from './pages/InvoiceDetail';

// Client Portal
import ClientPortal from './pages/client/ClientPortal';
import ClientQuotes from './pages/client/ClientQuotes';
import ClientInvoices from './pages/client/ClientInvoices';

// Read-Only Client Portal (Token-Based)
import ClientPortalView from './pages/ClientPortal';

// Communications Hub & Assistant-Life
import Communications from './pages/Communications';
import AssistantLife from './pages/AssistantLife';
import EmergencyMode from './pages/EmergencyMode';

// Company Calendar
import Calendar from './pages/Calendar';

// Legal & Trust Pages
import { TermsOfService, PrivacyPolicy, AIUseLimitations, EmergencyDisclaimer } from './pages/legal';

// Support & Legal Pages
import { SupportLegal, SystemStatus, HelpCentre, ContactSupport, AccessLog } from './pages/support';

// Account Pages
import { Profile, Billing, AccountSettings, TeamMembers } from './pages/account';

// Invite Acceptance
import AcceptInvite from './pages/AcceptInvite';

function App() {
  // Initialize theme on app load
  useEffect(() => {
    initializeTheme();
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes - Auth */}
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />
          <Route path="/forgot-password" element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } />
          <Route path="/reset-password" element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          } />
          <Route path="/loading" element={<LoadingScreen />} />
          
          {/* Legal & Trust Pages (accessible pre-login and post-login) */}
          <Route path="/legal/terms" element={<TermsOfService />} />
          <Route path="/legal/privacy" element={<PrivacyPolicy />} />
          <Route path="/legal/ai-use" element={<AIUseLimitations />} />
          <Route path="/legal/emergency-disclaimer" element={<EmergencyDisclaimer />} />

          {/* Invite Acceptance (public, token-based) */}
          <Route path="/invite/:token" element={<AcceptInvite />} />

          {/* Client Portal Routes (separate auth) */}
          <Route path="/client" element={<ClientPortal />} />
          <Route path="/client/quotes" element={<ClientQuotes />} />
          <Route path="/client/invoices" element={<ClientInvoices />} />
          <Route path="/client/emergency" element={<ClientPortal />} />
          
          {/* Read-Only Client Portal (Token-Based Access) */}
          <Route path="/client/:token" element={<ClientPortalView />} />

          {/* Protected Main App Routes with Layout */}
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            {/* Redirect root to command centre */}
            <Route index element={<Navigate to="/command-centre" replace />} />
            
            {/* Command Centre - Primary Landing */}
            <Route path="command-centre" element={<CommandCentre />} />
            
            {/* Company Calendar */}
            <Route path="calendar" element={<Calendar />} />
            
            {/* Onboarding - Protected */}
            <Route path="onboarding" element={<Onboarding />} />

            {/* Finance Module (includes Accounting / Supplier Upload) - Protected by Finance Access */}
            <Route path="finance" element={<FinanceRoute><Finance /></FinanceRoute>} />
            <Route path="finance/pots" element={<FinanceRoute><Pots /></FinanceRoute>} />
            <Route path="finance/cashflow" element={<FinanceRoute><Cashflow /></FinanceRoute>} />
            <Route path="finance/invoices" element={<FinanceRoute><FinanceInvoices /></FinanceRoute>} />
            <Route path="finance/margin" element={<FinanceRoute><Margin /></FinanceRoute>} />
            <Route path="finance/documents" element={<FinanceRoute><DocumentUpload /></FinanceRoute>} />
            <Route path="finance/price-intelligence" element={<FinanceRoute><PriceIntelligence /></FinanceRoute>} />
            <Route path="finance/price-intelligence/:category" element={<FinanceRoute><PriceIntelligence /></FinanceRoute>} />
            <Route path="finance/suppliers" element={<FinanceRoute><SupplierUpload /></FinanceRoute>} />
            <Route path="finance/xero" element={<FinanceRoute><FinanceXero /></FinanceRoute>} />
            <Route path="business-reality" element={<FinanceRoute><BusinessReality /></FinanceRoute>} />

            {/* Jobs Module */}
            <Route path="jobs" element={<Jobs />} />
            <Route path="jobs/:id" element={<JobDetail />} />

            {/* Quote Life Module (includes Estimates & Proposals) */}
            <Route path="quotes" element={<Quotes />} />
            <Route path="quotes/:id" element={<QuoteDetail />} />
            <Route path="quotes/estimates" element={<Estimates />} />
            <Route path="quotes/estimates/:id" element={<EstimateDetail />} />
            <Route path="quotes/proposals" element={<Proposals />} />
            <Route path="quotes/proposals/:id" element={<ProposalDetail />} />

            {/* Invoices Module */}
            <Route path="invoices" element={<Invoices />} />
            <Route path="invoices/:id" element={<InvoiceDetail />} />

            {/* Communications Hub & Assistant-Life */}
            <Route path="communications" element={<Communications />} />
            <Route path="assistant-life" element={<AssistantLife />} />
            <Route path="emergency" element={<EmergencyMode />} />

            {/* Account Pages */}
            <Route path="account/profile" element={<Profile />} />
            <Route path="account/billing" element={<Billing />} />
            <Route path="account/settings" element={<AccountSettings />} />
            <Route path="account/team-members" element={<TeamMembers />} />

            {/* Support & Legal Pages */}
            <Route path="support" element={<SupportLegal />} />
            <Route path="support/status" element={<SystemStatus />} />
            <Route path="support/help" element={<HelpCentre />} />
            <Route path="support/contact" element={<ContactSupport />} />
            <Route path="support/access-log" element={<AccessLog />} />

            {/* Legacy redirects for standalone routes */}
            <Route path="estimates" element={<Navigate to="/quotes/estimates" replace />} />
            <Route path="estimates/:id" element={<Navigate to="/quotes/estimates" replace />} />
            <Route path="proposals" element={<Navigate to="/quotes/proposals" replace />} />
            <Route path="proposals/:id" element={<Navigate to="/quotes/proposals" replace />} />
          </Route>

          {/* Catch all - redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
